import { useGetCurrentUserQuery } from "../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../app/accounts/memberships";
import { useGetChatThreadsQuery } from "../app/services/chatThreads";
import {
  isClientGroupPosition,
  isPrimaryClientPosition,
  isProducerGroupPosition,
  isSpecialistPosition,
} from "../utils/memberPositionUtils";

export interface UseProjectDmAccessReturn {
  /** DM タブ（下部ナビ / ⌥5 ショートカット）へアクセスできるか。 */
  canAccessDm: boolean;
}

export interface UseProjectDmAccessOptions {
  /**
   * 呼び出し側で既にポジションが分かっている場合に渡す（横断タスクボードの
   * 一覧 API `current_user_role` 等）。指定時はメンバーシップ API を呼ばない。
   */
  position?: string;
}

/**
 * プロジェクト内の DM タブへのアクセス可否。
 * BottomAppBarProject（下部ナビの表示）と useProjectShortcuts（⌥5）で共用し、
 * タブ表示とショートカット可否を常に一致させる。
 *
 * - プロデューサーグループ（PRODUCER / DIRECTOR / ADMIN）: 常に可
 * - クライアント責任者（CLIENT）: 常に可
 * - SPECIALIST: 常に可（アクセスできるのが DM のみ）
 * - REVIEWER（クライアントグループの非責任者）: 参加している Chat
 *   （オファー非依存 DM）スレッドが 1 件以上あるときのみ可。
 *   閲覧・返信のみで、スレッド作成は DM ページ側の canCreateDm
 *   （isPrimaryPosition）が引き続き責任者に限定する。
 *
 * BE の `GET /api/me/<project_id>/chats/` は参加者フィルタ
 * （thread_members__user=user）のみで position を問わないため、REVIEWER でも
 * 自分が参加するスレッドの有無（count）を取得できる。
 */
export const useProjectDmAccess = (
  projectId: string | undefined,
  options?: UseProjectDmAccessOptions,
): UseProjectDmAccessReturn => {
  const knownPosition = options?.position;
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(
    projectId ?? "",
    { skip: !projectId || !currentUserData || !!knownPosition },
  );
  const position: string | undefined =
    knownPosition ?? membershipData?.data?.position ?? undefined;

  const isReviewer =
    !!position &&
    isClientGroupPosition(position) &&
    !isPrimaryClientPosition(position);

  // 参加スレッドの有無だけ分かればよいので limit=1 で取得する。
  // REVIEWER 以外はタブ可否がポジションだけで決まるためリクエストしない。
  const { data: chatThreadsData } = useGetChatThreadsQuery(
    { project_id: projectId ?? "", limit: 1 },
    { skip: !projectId || !currentUserData || !isReviewer },
  );

  if (!position) return { canAccessDm: false };
  if (isSpecialistPosition(position)) return { canAccessDm: true };
  if (isProducerGroupPosition(position)) return { canAccessDm: true };
  if (isPrimaryClientPosition(position)) return { canAccessDm: true };
  if (isReviewer) {
    return { canAccessDm: (chatThreadsData?.data?.count ?? 0) > 0 };
  }
  return { canAccessDm: false };
};

export default useProjectDmAccess;
